import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import OrderService from "../services/order.service";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import AnimateHeight from "react-animate-height";
import ChooseAddress from "./ChooseAddress";
import { selectCurrentNewHireKitItems, getNewHireKitItems, selectInventory } from '../reducers/catalog';
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Dropdown, DropdownButton, Alert } from 'react-bootstrap';
import ConfirmNewHireKitModal from "./modals/ConfirmNewHireKitModal";
import NewHireKitOrderConfirmation from "./admin/NewHireKitOrderConfirmation";
import KitItem from "./KitItem";
import { reloadUser, getNhKGate, getUser, getSubsite } from "../reducers/auth";
import axios from "../reducers/authinterceptor";
import AuthHeader from "../services/auth.header.js";
import { resolveRetailHomePath } from "../utils/retailHome";
import { buildNHKChoiceGroups } from "../utils/nhkChoiceGroups";

/**
 * Storefront NHK page for users locked by the new-hire kit gate.
 * No pricing shown. Places order via /api/order/newhirekit then unlocks the site.
 * Polybag is always applied with the user's name (no UI option).
 */
const NewHireKitHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const catalogItem = useSelector(selectCurrentNewHireKitItems);
  const inventory = useSelector(selectInventory);
  const itemStatus = useSelector(state => state.catalog.adminItemStatus);
  const nhkGate = useSelector(getNhKGate);
  const currentUser = useSelector(getUser);
  const currentSubsite = useSelector(getSubsite);

  const [allKits, setAllKits] = useState([]);
  const [currentKit, setCurrentKit] = useState(null);
  const [loadingKits, setLoadingKits] = useState(true);
  const [productList, setProductList] = useState([]);
  const [allSelectedSizes, setAllSelectedSizes] = useState([]);
  const [height, setHeight] = useState(0);
  const [newAddress, setNewAddress] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [completedOrderSnapshot, setCompletedOrderSnapshot] = useState(null);

  const userPolybagLabel = [currentUser?.firstname, currentUser?.lastname].filter(Boolean).join(" ").trim()
    || currentUser?.username
    || "";

  useEffect(() => {
    const loadKits = async () => {
      setLoadingKits(true);
      try {
        const response = await axios.get(
          process.env.REACT_APP_API_URL + "/newhirekits",
          { headers: AuthHeader.authHeader() }
        );
        const kits = response.data || [];
        setAllKits(kits);
        if (kits.length > 0) setCurrentKit(kits[0]);
      } catch (err) {
        console.error(err);
        setErrorMessage("Could not load new hire kits.");
      }
      setLoadingKits(false);
    };
    loadKits();
  }, []);

  useEffect(() => {
    if (currentKit) {
      dispatch(getNewHireKitItems(currentKit.id));
    }
  }, [currentKit, dispatch]);

  useEffect(() => {
    if (itemStatus === 'succeeded') {
      if (catalogItem && catalogItem.items && catalogItem.items.length > 0) {
        setProductList(buildNHKChoiceGroups(catalogItem.items));
      } else {
        setProductList([]);
      }
    }
  }, [itemStatus, catalogItem]);

  const setSize = (itemIdx, productId) => {
    let newSelectedSizes = [];
    if (!allSelectedSizes || allSelectedSizes.length === 0) {
      newSelectedSizes = new Array(productList.length);
    } else {
      for (var i = 0; i < productList.length; i++) {
        newSelectedSizes[i] = allSelectedSizes[i];
      }
    }
    newSelectedSizes[itemIdx] = (productId == "" || productId == null) ? null : productId;
    setAllSelectedSizes([...newSelectedSizes]);
    setErrorMessage(null);
  };

  const toggleExpansion = () => {
    var expanded = isExpanded;
    setIsExpanded(!isExpanded);
    setHeight(expanded ? 0 : 'auto');
  };

  const closeAddressModal = async (update, address) => {
    toggleExpansion();
    if (update) {
      setNewAddress(address);
      setErrorMessage(null);
    }
  };

  const submitNewHireKit = async () => {
    setSavingOrder(true);
    var newOrderItems = [];
    var error = false;
    var msg = "";

    if (allSelectedSizes == null || allSelectedSizes.length == 0 || allSelectedSizes.length != productList.length) {
      msg = "Please select a size for all items. ";
      error = true;
    }
    for (var j = 0; j < (allSelectedSizes || []).length; j++) {
      if (allSelectedSizes[j] == null) {
        msg = "Please select a size for all items. ";
        error = true;
        break;
      }
    }
    if (newAddress == null || newAddress.name == null) {
      msg += "Please add a shipping address. ";
      error = true;
    }
    if (error) {
      setErrorMessage(msg);
      setSavingOrder(false);
      return;
    }

    for (var j = 0; j < allSelectedSizes.length; j++) {
      var selectedSizeProductId = allSelectedSizes[j];
      var selectedCatalogItem = null;
      for (var i = 0; i < catalogItem.items.length; i++) {
        if (catalogItem.items[i].sizes.find(size => size.productId == selectedSizeProductId)) {
          selectedCatalogItem = catalogItem.items[i];
          break;
        }
      }
      var selectedSize = selectedCatalogItem.sizes.find(size => size.productId == selectedSizeProductId);
      newOrderItems.push({
        productId: selectedSizeProductId,
        decorations: selectedCatalogItem.logos,
        item: selectedCatalogItem,
        selectedSize: selectedSize.sizeName
      });
    }
    setOrderItems(newOrderItems);
    setShowConfirmation(true);
    setSavingOrder(false);
  };

  const confirm = async () => {
    try {
      setSavingOrder(true);
      const order = await OrderService.orderNewHireKitSelf(
        orderItems,
        newAddress,
        true,
        userPolybagLabel,
        currentKit?.id
      );
      setCompletedOrderSnapshot({
        items: orderItems,
        address: newAddress,
        polybagChecked: true,
        polybagLabel: userPolybagLabel
      });
      setCompletedOrder(order);
      setShowConfirmation(false);
      await dispatch(reloadUser());
    } catch (err) {
      console.error(err);
      setErrorMessage(err?.response?.data?.message || err.message || "Failed to place New Hire Kit order.");
      setShowConfirmation(false);
    } finally {
      setSavingOrder(false);
    }
  };

  const goToSite = async () => {
    const path = await resolveRetailHomePath(currentSubsite);
    navigate(path);
  };

  if (loadingKits) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  const content = productList.map((group, idx) => (
    <KitItem
      key={group.id || idx}
      sizeIdx={idx}
      options={group.options}
      setSize={setSize}
      inventory={inventory || []}
      remove={null}
      edit={true}
      clearErrors={() => setErrorMessage(null)}
      groupTitle={group.isSolo ? null : group.title}
    />
  ));

  return (
    <Container className="py-3">
      {nhkGate?.status === 'locked' && (
        <Alert variant="info">
          Welcome! Please select and order your new hire kit
          {nhkGate.daysLeft != null ? ` (you have ${nhkGate.daysLeft} day(s) remaining)` : ''}.
        </Alert>
      )}

      {completedOrder && completedOrderSnapshot && (
        <NewHireKitOrderConfirmation
          order={completedOrder}
          items={completedOrderSnapshot.items}
          address={completedOrderSnapshot.address}
          polybagChecked={completedOrderSnapshot.polybagChecked}
          polybagLabel={completedOrderSnapshot.polybagLabel}
          onDone={goToSite}
          doneLabel="Continue to the store"
        />
      )}

      {!completedOrder && (
        <>
          <Row className="mb-3 align-items-center">
            <Col xs="auto">
              <h2>New Hire Kit</h2>
            </Col>
            {allKits.length > 1 && (
              <Col xs="auto">
                <DropdownButton
                  id="kit-selector"
                  title={currentKit ? currentKit.title : "Select Kit"}
                  variant="outline-primary"
                >
                  {allKits.map(kit => (
                    <Dropdown.Item
                      key={kit.id}
                      onClick={() => {
                        setCurrentKit(kit);
                        setAllSelectedSizes([]);
                      }}
                      active={currentKit?.id === kit.id}
                    >
                      {kit.title}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
              </Col>
            )}
            {allKits.length === 1 && currentKit && (
              <Col><strong>{currentKit.title}</strong></Col>
            )}
          </Row>
          <hr />

          {allKits.length === 0 && (
            <Alert variant="warning">No new hire kits are configured for this site yet. Please contact your administrator.</Alert>
          )}

          {currentKit && (
            <>
              <h4>Shipping Address</h4>
              <div style={{ marginLeft: 30 }}>
                <AnimateHeight duration={500} height={height == 0 ? 'auto' : 0}>
                  {newAddress && (
                    <div>
                      <h2>Delivering to {newAddress.name}</h2>
                      <p style={{ marginLeft: 30, padding: 5, border: "1px solid #CCCCCC" }}>
                        {newAddress.street}<br />
                        {newAddress.street2 != null && newAddress.street2 != "" && <>{newAddress.street2}<br /></>}
                        {newAddress.city}, {newAddress.state} {newAddress.zip}<br />
                      </p>
                    </div>
                  )}
                  <div><Button onClick={toggleExpansion}>Set Address</Button></div>
                </AnimateHeight>
                <AnimateHeight duration={500} height={height}>
                  <ChooseAddress close={closeAddressModal} />
                </AnimateHeight>
              </div>
              <hr />
              {itemStatus === 'loading' ? <Spinner animation="border" /> : content}
              <hr />
              <Button onClick={submitNewHireKit} variant="primary" disabled={savingOrder} style={{ marginTop: 20 }}>
                {savingOrder ? 'Submitting…' : 'Confirm New Hire Kit'}
              </Button>
              {errorMessage && <div className="error" style={{ marginTop: 10 }}>{errorMessage}</div>}
              {showConfirmation && orderItems && (
                <ConfirmNewHireKitModal
                  show={showConfirmation}
                  close={() => setShowConfirmation(false)}
                  confirm={confirm}
                  items={orderItems}
                  address={newAddress}
                  polybagChecked={true}
                  polybagLabel={userPolybagLabel}
                />
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default NewHireKitHome;
